// 顧客リソースの宣言。既存の customer.service をそのまま呼ぶ（ブラウザ側＝本人の権限で実行）。
// LLM へ渡す項目は厳選し、件数にも上限を設ける（小型モデルの文脈を溢れさせない）。
import { z } from 'zod';
import {
  fetchCustomers,
  createCustomer,
  updateCustomer,
  type CustomerCreateInput,
  type CustomerUpdateInput,
} from '@modules/customer/services/customer.service';
import type { Customer, ID } from '@shared/types';
import { CUSTOMER_STATUS, type ListFilters, type ResourceDescriptor, type WriteResult } from './types';
import { MAX_ROWS, normalizeCode } from './shared';

/** 顧客1件を、LLM が読む最小限の形へ射影する（住所・連絡先などは渡さない）。 */
function toCompactCustomer(c: Customer) {
  return {
    id: c.id, // 書込時に customerId として使うため、一覧にも id を含める
    code: c.code,
    name: c.name,
    rank: c.rank,
    status: c.status,
    industry: c.industry,
  };
}

async function list(filters: ListFilters): Promise<unknown> {
  const customers = await fetchCustomers({ status: filters.status, rank: filters.rank });

  const keyword = filters.keyword;
  const filtered = keyword
    ? customers.filter((c) => c.name.includes(keyword) || c.nameKana.includes(keyword))
    : customers;

  return {
    count: filtered.length,
    truncated: filtered.length > MAX_ROWS,
    customers: filtered.slice(0, MAX_ROWS).map(toCompactCustomer),
  };
}

async function get(rawCode: string): Promise<unknown> {
  const code = normalizeCode(rawCode);
  const customers = await fetchCustomers();
  const found = customers.find((c) => normalizeCode(c.code) === code);

  if (!found) {
    return { found: false, note: `顧客コード「${rawCode}」は見つかりませんでした` };
  }

  return {
    found: true,
    // 連絡先（電話・メール・住所）は一覧と同じく渡さない。ログインユーザー自身は
    // 本体画面で見られる情報だが、LLMへ渡すとプロンプトインジェクション等での
    // 抽出リスクが生じるため「圧縮射影・機微項目除外」の方針を貫く。
    customer: {
      ...toCompactCustomer(found),
      nameKana: found.nameKana,
    },
  };
}

// ── 書込（C-2）─────────────────────────────────────────────────
// AIが触れる項目は本体スキーマの部分集合のみ。ランク・区分・締め日・税計算モード等の
// 業務設定は触らせない（本体の superRefine で TEL・郵便番号は顧客区分では必須）。

const writeCreateSchema = z.object({
  name: z.string().min(1, 'name（顧客名）は必須'),
  nameKana: z.string().min(1, 'nameKana（フリガナ）は必須'),
  status: z.enum(CUSTOMER_STATUS),
  phone: z.string().min(1, 'phone（電話番号）は必須'),
  postalCode: z.string().min(1, 'postalCode（郵便番号）は必須'),
  prefecture: z.string().optional(),
  industry: z.string().optional(),
  email: z.string().email('email の形式が不正').optional(),
  notes: z.string().optional(),
});

const writeUpdateSchema = writeCreateSchema
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'changes に変更する項目を1つ以上入れる' });

export const customerResource: ResourceDescriptor = {
  name: 'customer',
  label: '顧客',
  list,
  get,
  write: {
    fields: [
      { name: 'name', label: '顧客名', type: 'string', required: true },
      { name: 'nameKana', label: 'フリガナ', type: 'string', required: true, note: '全角カタカナ' },
      { name: 'status', label: 'ステータス', type: 'enum', required: true, values: CUSTOMER_STATUS, note: 'active取引中/inactive休止/prospect見込み' },
      { name: 'phone', label: '電話番号', type: 'string', required: true },
      { name: 'postalCode', label: '郵便番号', type: 'string', required: true },
      { name: 'prefecture', label: '都道府県', type: 'string' },
      { name: 'industry', label: '業種', type: 'string' },
      { name: 'email', label: 'メール', type: 'string' },
      { name: 'notes', label: '備考', type: 'string' },
    ],
    createSchema: writeCreateSchema,
    updateSchema: writeUpdateSchema,
    async create(data, userId: ID): Promise<WriteResult> {
      // 残りの必須項目（rank/partnerTypes等）は本体スキーマの default が埋める
      const created = await createCustomer(
        { ...data, aiPendingReview: true } as CustomerCreateInput,
        userId
      );
      return { id: created.id, code: created.code };
    },
    async update(code, changes, userId: ID): Promise<WriteResult | null> {
      const normalized = normalizeCode(code);
      const customers = await fetchCustomers();
      const target = customers.find((c) => normalizeCode(c.code) === normalized);
      if (!target) return null;
      await updateCustomer(
        target.id,
        { ...changes, aiPendingReview: true } as CustomerUpdateInput,
        userId
      );
      return { id: target.id, code: target.code };
    },
    link: (id) => `/customers/${id}`,
  },
};
